// Чистая логика блока товаров магазина (ChatBlock 'shop'): отбор карточек и
// сборка тела запроса к корзине САЙТА. DOM и fetch здесь нет намеренно —
// модуль тестируется в изоляции (shop.test.mjs), как gallery.ts.
// import type (а не обычный import): под `node --test --experimental-strip-types`
// тип стирается вместе со строкой импорта — тесту не нужен рантайм api.ts.
import type { ShopCard, ShopConfig } from './api';

/** Потолок карточек на блок — зеркало серверного Capabilities::Shop::MAX_CARDS.
 *  Клиентская страховка §12: сколько бы ни приехало, лента конечна. */
export const SHOP_MAX_CARDS = 6;

/** Разделитель составного id позиции: «composition:13» — тип и номер. Так
 *  устроены магазины, где корзина принимает две части отдельными полями. */
const ID_SEPARATOR = ':';

/** Карточки к отрисовке: без картинки и заголовка карточка бессмысленна
 *  (пустой src даёт «битое фото» на чужом сайте, заголовок — это alt и
 *  доступное имя). Лишние сверх потолка отбрасываем. */
export function shopCards(items: ShopCard[]): ShopCard[] {
  return items.filter((card) => Boolean(card?.image) && Boolean(card?.title)).slice(0, SHOP_MAX_CARDS);
}

/** Поля формы для корзины магазина: «composition:13» → { product_type:
 *  'composition', product_id: '13' }. Имена полей задаёт владелец в кабинете
 *  (Assistants::ShopSettings) — гадать про чужую корзину мы не можем.
 *
 *  null — класть нечем: у карточки нет id, владелец не описал корзину или не
 *  назвал поле идентификатора. Кнопки «в корзину» в этом случае не будет.
 *
 *  Составной id разбираем ТОЛЬКО когда владелец объявил поле типа: иначе
 *  двоеточие — часть самого идентификатора («sku:12-34» бывает целым ключом),
 *  и разрезать его значит отправить магазину половину. */
export function cartFields(
  card: ShopCard,
  config: ShopConfig | null | undefined,
): Record<string, string> | null {
  const id = (card?.id ?? '').trim();
  if (!id || !config?.cart_url || !config.id_field) return null;

  const cut = id.indexOf(ID_SEPARATOR);
  const split = cut > 0 && cut < id.length - 1 && Boolean(config.type_field);
  const fields: Record<string, string> = {};
  fields[config.id_field] = split ? id.slice(cut + 1) : id;
  if (split && config.type_field) fields[config.type_field] = id.slice(0, cut);
  return fields;
}

/** Скрытое поле защиты от CSRF, которое страница магазина уже отрисовала в
 *  своей форме. Берём ЕГО, а не выдумываем своё: запрос к корзине уходит от
 *  имени той же страницы и с её же сессией, и сервер владельца обязан его
 *  проверять. Имя поля у всех разное (_csrf, authenticity_token, csrf_token),
 *  поэтому ищем по образцу имени, а не по одному конкретному ключу.
 *
 *  Пусто — на странице такого поля нет: значит корзина его и не требует. */
export function csrfField(doc: Document): Record<string, string> {
  const input = doc.querySelector<HTMLInputElement>(
    'input[type="hidden"][name*="csrf" i], input[type="hidden"][name*="authenticity_token" i]',
  );
  return input?.name && input.value ? { [input.name]: input.value } : {};
}

/** Тело запроса к корзине: поля позиции + защита страницы, в формате обычной
 *  формы. Именно формой, а не JSON: «В корзину» на сайте — это <form method=
 *  post>, и сервер владельца уже умеет её принимать. */
export function cartBody(fields: Record<string, string>, csrf: Record<string, string>): string {
  return new URLSearchParams({ ...csrf, ...fields }).toString();
}

/** Нужно ли идти за токеном на страницу товара: на текущей его нет.
 *
 *  Поле защиты живёт в форме «В корзину», а такая форма есть не на каждой
 *  странице: у магазина шаров она в каталоге, но не на главной и не в «О нас».
 *  Живой сайт 06.08.2026: из чата на главной корзина отвечала отказом, потому
 *  что виджету нечего было приложить. Токен в этом случае берём со страницы
 *  САМОГО товара — она у карточки есть и форму содержит всегда. */
export function needsRemoteToken(csrf: Record<string, string>, card: { url?: string }): boolean {
  return Object.keys(csrf).length === 0 && Boolean(card?.url);
}
